import { COLORS, COLOR_ORDER, FACE_CONFIG, FACE_ORDER } from './cube-config.js';
import { CENTER_INDICES_4X4, STICKERS_PER_FACE_4X4, TOTAL_STICKERS_4X4 } from './cube-config-4x4.js';
import {
  CORNER_FACELETS_4X4,
  CORNER_PIECE_BY_KEY_4X4,
  CORNER_POSITION_NAMES_4X4,
  WING_FACELETS_4X4,
  WING_PIECE_BY_DIRECTED_KEY_4X4,
  WING_POSITION_NAMES_4X4,
  pieceKey4x4,
} from './cubie-config-4x4.js';
import { deriveOrientation4x4, validateOrientation4x4 } from './orientation-4x4.js';

function error(code, message, details = {}) {
  return { code, message, details };
}

function colorLabelFromFaces(faces, orientation) {
  return [...faces].map((face) => COLORS[orientation.faceToColor[face]].name.replace('色', '')).join('');
}

function rotateLeft(value, amount) {
  return value.slice(amount) + value.slice(0, amount);
}

function completenessErrors(stickers, rawOrientation) {
  const orientationErrors = validateOrientation4x4(rawOrientation);
  if (orientationErrors.length) return orientationErrors.map((item) => error(item.code, item.message));
  const errors = [];
  if (!stickers || typeof stickers !== 'object') return [error('STICKER_DATA_MISSING', '没有可校验的 4×4 贴纸数据')];
  for (const face of FACE_ORDER) {
    if (!Array.isArray(stickers[face]) || stickers[face].length !== STICKERS_PER_FACE_4X4) {
      errors.push(error('FACE_LENGTH', `${face} 面必须恰好包含 16 张贴纸`, { face }));
    }
  }
  if (errors.length) return errors;
  const all = FACE_ORDER.flatMap((face) => stickers[face]);
  const missing = all.filter((color) => color === null || typeof color === 'undefined').length;
  if (missing) errors.push(error('MISSING_STICKERS', `仍有 ${missing} 张贴纸未填写`, { missing }));
  const unknown = all.filter((color) => color !== null && !COLOR_ORDER.includes(color));
  if (unknown.length) errors.push(error('UNKNOWN_COLOR', `检测到 ${unknown.length} 张未知颜色贴纸`));
  const counts = Object.fromEntries(COLOR_ORDER.map((color) => [color, 0]));
  for (const color of all) if (color in counts) counts[color] += 1;
  for (const color of COLOR_ORDER) {
    const count = counts[color];
    if (count === STICKERS_PER_FACE_4X4) continue;
    const delta = Math.abs(STICKERS_PER_FACE_4X4 - count);
    errors.push(error(
      'COLOR_COUNT',
      `${COLORS[color].name}贴纸有 ${count} 张，${count < 16 ? `还缺少 ${delta} 张` : `多出 ${delta} 张`}`,
      { color, count },
    ));
  }
  if (all.length !== TOTAL_STICKERS_4X4) errors.push(error('TOTAL_COUNT', '4×4 必须恰好包含 96 张贴纸'));
  return errors;
}

function readLogicalFaces(stickers, facelets, orientation) {
  return facelets.map(([face, index]) => orientation.colorToFace[stickers[face][index]]).join('');
}

function validatePieces(stickers, orientation) {
  const errors = [];
  const cornerPieces = [];
  const cornerOrientation = [];
  for (let position = 0; position < CORNER_FACELETS_4X4.length; position += 1) {
    const observed = readLogicalFaces(stickers, CORNER_FACELETS_4X4[position], orientation);
    const piece = CORNER_PIECE_BY_KEY_4X4[pieceKey4x4(observed)];
    if (typeof piece === 'undefined') {
      errors.push(error('INVALID_CORNER', `检测到不可能存在的${colorLabelFromFaces(observed, orientation)}角块`, { position, observed }));
      continue;
    }
    const canonical = CORNER_FACELETS_4X4[piece].map(([face]) => face).join('');
    const twist = [0, 1, 2].find((amount) => rotateLeft(canonical, amount) === observed);
    if (typeof twist === 'undefined') {
      errors.push(error('MIRRORED_CORNER', `${colorLabelFromFaces(observed, orientation)}角块的贴纸顺序发生镜像`, { position, observed }));
      continue;
    }
    cornerPieces.push(piece);
    cornerOrientation.push(twist);
  }

  const wingPieces = [];
  for (let position = 0; position < WING_FACELETS_4X4.length; position += 1) {
    const observed = readLogicalFaces(stickers, WING_FACELETS_4X4[position], orientation);
    const piece = WING_PIECE_BY_DIRECTED_KEY_4X4[observed];
    if (typeof piece === 'undefined') {
      errors.push(error('INVALID_WING', `检测到不可能存在的${colorLabelFromFaces(observed, orientation)}棱翼组合`, { position, observed }));
      continue;
    }
    wingPieces.push(piece);
  }

  for (const [kind, pieces, names] of [
    ['CORNER', cornerPieces, CORNER_POSITION_NAMES_4X4],
    ['WING', wingPieces, WING_POSITION_NAMES_4X4],
  ]) {
    const counts = new Map();
    for (const piece of pieces) counts.set(piece, (counts.get(piece) ?? 0) + 1);
    for (let piece = 0; piece < names.length; piece += 1) {
      const count = counts.get(piece) ?? 0;
      if (count > 1) errors.push(error(`DUPLICATE_${kind}`, `检测到重复的${kind === 'CORNER' ? '角块' : '棱翼'}：${names[piece]}`, { piece, count }));
      if (count === 0) errors.push(error(`MISSING_${kind}`, `缺少${kind === 'CORNER' ? '角块' : '棱翼'}：${names[piece]}`, { piece }));
    }
  }

  const centerCounts = Object.fromEntries(COLOR_ORDER.map((color) => [color, 0]));
  for (const face of FACE_ORDER) {
    for (const index of CENTER_INDICES_4X4) centerCounts[stickers[face][index]] += 1;
  }
  for (const color of COLOR_ORDER) {
    if (centerCounts[color] !== 4) {
      errors.push(error('CENTER_COUNT', `${COLORS[color].name}可移动中心块应为 4 张，当前为 ${centerCounts[color]} 张`, { color, count: centerCounts[color] }));
    }
  }
  return { errors, cornerPieces, cornerOrientation, wingPieces, centerCounts };
}

export function stickersToSolverFacelets4x4(stickers, orientation) {
  const derived = orientation.faceToColor ? orientation : deriveOrientation4x4(orientation);
  return FACE_ORDER.flatMap((face) => stickers[face].map((color) => FACE_ORDER.indexOf(derived.colorToFace[color])));
}

export function validateCubeState4x4(stickers, rawOrientation) {
  const level1Errors = completenessErrors(stickers, rawOrientation);
  if (level1Errors.length) return { valid: false, level: 'completeness', errors: level1Errors };
  const orientation = rawOrientation.faceToColor ? rawOrientation : deriveOrientation4x4(rawOrientation);
  const reconstructed = validatePieces(stickers, orientation);
  if (reconstructed.errors.length) return { valid: false, level: 'pieces', errors: reconstructed.errors };
  if (reconstructed.cornerOrientation.reduce((sum, value) => sum + value, 0) % 3 !== 0) {
    return {
      valid: false,
      level: 'reachability',
      errors: [error('CORNER_TWIST', '角块朝向总和不合法，检测到无法由真实转动形成的角块扭转')],
    };
  }
  return {
    valid: true,
    level: 'solver-compatible',
    errors: [],
    orientation,
    solverFacelets: stickersToSolverFacelets4x4(stickers, orientation),
    pieces: reconstructed,
  };
}

export function isSolved4x4(stickers, orientation) {
  const derived = orientation.faceToColor ? orientation : deriveOrientation4x4(orientation);
  return FACE_ORDER.every((face) => stickers[face].every((color) => color === derived.faceToColor[face]));
}

export function canonicalColorForLogicalFace4x4(face) {
  return FACE_CONFIG[face].color;
}

